/**
 * @module support/shared/widgets
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */

/* tslint:disable:no-forward-ref */
/*tslint:disable max-file-line-count*/
// Core
import {
  Component,
  OnInit,
  Input,
  EventEmitter,
  Output,
  Provider,
  forwardRef,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { PrimeNgEvent } from '@core/public';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

// Interface
import { AutoCompleteMapping } from '@support/shared/widgets/interfaces/autocomplete.interface';

// Constant
import { INPUT_AUTOCOMPLETE_CONSTANTS } from '@support/shared/widgets/constants/widgets.constants';

// Service
import { CommonApiService } from '@support/shared/services/common-api/common-api.service';

// Model
import { FieldValidationItem } from '@support/shared/models/field-validation-item.model';
import { AutoComplete } from 'primeng/autocomplete';
/**
 * Provider Expression that allows tmo-checkbox to register as a ControlValueAccessor.
 * This allows it to support [(ngModel)].
 * @docs-private
 */
export const AUTOCOMPLETE_CONTROL_VALUE_ACCESSOR: Provider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => InputAutocompleteComponent),
  multi: true,
};
@Component({
  selector: 'tmo-input-autocomplete',
  templateUrl: './input-autocomplete.component.html',
  styleUrls: ['../../../../../../style-sheets/components/input-autocomplete.component.scss'],
  providers: [AUTOCOMPLETE_CONTROL_VALUE_ACCESSOR],
})
export class InputAutocompleteComponent
  implements OnInit, ControlValueAccessor, OnDestroy, OnChanges
{
  /**
   * Event emitted when the input is changed.
   * It emits the 'Event' object.
   */
  @Output() public selectionChanged: EventEmitter<Object>;
  /**
   * String input property to show the legalText title.
   */
  @Input() public brands: AutoCompleteMapping[];
  /**
   * Attribute to display placeholder text when the input is not filled in
   */
  @Input() public placeHolder: string;
  /**
   * Users can specify this field to impose the type of the field this input element is
   * and to fetch error message if it is dirty
   */
  @Input() public fieldType?: string;
  /**
   * String property to set Max Length
   */
  @Input() public maxlength: string;
  /**
   * String property to set Max Length
   */
  @Input() public minlength: string;
  /**
   * String property to match input with given pattern,  if used with a <form>
   */
  @Input() public pattern: string;
  /**
   * Users can specify this field to impose the type of the field this input element is
   * and to fetch error message if it is dirty
   */
  @Input() public feildValue?: string;
  /**
   * This field determines if the element is dirty or not as per the validations applied
   */
  @Input() public isInputDirty?: boolean;
  /**
   * Event emitted when the input is tabbed out.
   * It emits the 'Event' object.
   */
  @Output() public blur: EventEmitter<string>;
  /**
   * Event emitted when the input is tabbed out.
   * It emits the 'Event' object.
   */
  @Output() public onSelect: EventEmitter<string>;
  /**
   * Attribute to display the validation error text
   */
  @Input() public validationErrorText?: string;
  /**
   * Attribute to display the validation error text
   */
  @Input() public disabled?: boolean;
  /**
   * Attribute to identify dropdown mode on initial click
   */
  @Input() public enableQuickDropDownMode?: boolean;

  /**
   * Autohighlight if only one option in list
   */
  @Input() public autoHighlight?: boolean;
  /**
   * A unique vaule to be assigned as an id of the focus input to match a label defined for the component.
   */
  @Input() public id?: string;

  /**
   * Filtered list of autocomplete
   */
  public filteredList: string[];
  public placeHolderValue: string;
  public validationError: boolean;
  public selection: string;
  public formControlName: string;
  public fieldValidationObject: FieldValidationItem;

  /** Internal property to store current state of input box. */
  private innerValue: string;
  @ViewChild('autocomplete', { static: true }) public autoComplete: AutoComplete;

  /**
   * Initialize component variables
   * @param commonApiService
   */
  public constructor(private commonApiService: CommonApiService) {
    this.fieldType = '';
    this.feildValue = '';
    this.isInputDirty = false;
    this.blur = new EventEmitter<string>();
    this.onSelect = new EventEmitter<string>();
    this.innerValue = '';
    this.selectionChanged = new EventEmitter();
  }

  /**
   *  Filter the input with brand selections, and save matched results into filteredList
   * @param event
   */
  public filterBrands(event?: PrimeNgEvent): void {
    this.filteredList = [];
    const brandsLength = this.brands.length;
    const queryString = event ? event.query : this.value;
    for (let i = 0; i < brandsLength && queryString.length; i++) {
      this.selection = this.brands[i].key || this.brands[i].memberId;
      if (!/queryString/i.test(this.selection)) {
        this.filteredList.push(this.selection);
      }
    }
  }

  /**
   * Called whenever the input is focused out
   */
  public onBlur(): void {
    if (this.enableQuickDropDownMode) {
      if (this.autoComplete.suggestions.length === 1) {
        this.autoComplete.value = this.autoComplete.suggestions[0];
        this.innerValue = this.autoComplete.suggestions[0];
        this.validationError = false;
      } else if (this.isInputDirty) {
        this.autoComplete.value = '';
        this.autoComplete.inputEL.nativeElement.value = '';
      }
    }

    this.validateInput();
    this.blur.emit(this.innerValue);
    this.onTouchedCallback();
  }

  /**
   * Function called whenever input value changes
   * @param changes - Angular interface SimpleChanges
   * @docs - public
   */
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.isInputDirty) {
      const chng = changes.isInputDirty;
      const cur = JSON.stringify(chng.currentValue);
      const prev = JSON.stringify(chng.previousValue);
      if (cur !== prev && !changes.isInputDirty.firstChange) {
        this.validateInput();
      }
    }
    if (changes && changes.brands && this.autoComplete) {
      this.filterBrands();
    }
  }

  /**
   * Called whenever the input is focused out
   */
  public onSelection(): void {
    this.validateInput();
    this.onSelect.emit(this.innerValue);

    this.onTouchedCallback();
  }
  /**
   * Called whenever the input is changed
   */
  public onSelectChange(): void {
    this.validateInput();

    this.selectionChanged.emit(this.innerValue);
  }

  /**
   * Initializes placeHolderValue and the fieldValidationObject
   */
  public ngOnInit(): void {
    this.placeHolderValue = this.placeHolder;
    if (this.fieldType) {
      this.fieldValidationObject = this.commonApiService.getFieldValidation(this.fieldType);
    }
    if (this.enableQuickDropDownMode) {
      this.filteredList = this.getBrandsAsFilteredList();
    }
  }

  /**
   *  Remove all subscriptions on component destroy hook
   */
  public ngOnDestroy(): void {}

  /**
   *  ngModel accessor to get the value of property [based on input type]
   * @docs-private
   */
  get value(): string {
    return this.innerValue;
  }

  /**
   *  ngModel accessor to set the value
   * @param value - {} value: could be number or string
   * @docs - private
   */
  set value(value: string) {
    if (this.enableQuickDropDownMode && value === '') {
      this.filteredList = this.getBrandsAsFilteredList();
    }
    if (value !== this.innerValue) {
      this.innerValue = value;
      this.validateInput();
      this.onChangeCallback(value);
    }
  }

  /**
   * Sets the model value. Implemented as part of ControlValueAccessor.
   * @param value Value to be set to the model.
   */
  public writeValue(value: string): void {
    if (value !== this.innerValue) {
      this.validateInput();
      this.innerValue = value;
      value = this.innerValue;
      this.onChangeCallback(value);
    }
  }

  /**
   *  Registers a callback to be triggered when the value has changed.
   * Implemented as part of ControlValueAccessor.
   * @param fn Function to be called on change.
   */
  public registerOnChange(fn: (_: boolean) => {}): void {
    this.onChangeCallback = fn;
  }

  /**
   *  Registers a callback to be triggered when the control has been touched.
   * Implemented as part of ControlValueAccessor.
   * @param fn Callback to be triggered when the input is touched.
   */
  public registerOnTouched(fn: () => {}): void {
    this.onTouchedCallback = fn;
  }

  /**
   *  Called when the input is blurred. Needed to properly implement ControlValueAccessor.
   * @docs-private
   */
  private onTouchedCallback(): void {}

  /**
   * Called when the input value is changed. Needed to properly implement ControlValueAccessor.
   * @docs-private
   */
  private onChangeCallback(_: {}): void {}

  /**
   * Validate the input and determine the error messages
   */
  private validateInput(): void {
    const key = 'default';
    const errorMessage = 'errorMessage';
    this.validationError = this.isInputDirty;
    // @TODO refactor it to not apply the validation always
    if (this.fieldType !== INPUT_AUTOCOMPLETE_CONSTANTS.BYPASS) {
      this.validationErrorText = this.isInputDirty
        ? this.commonApiService.getFieldValidation(this.fieldType).errorMessages[key][errorMessage]
        : '';
    }
  }

  /**
   * Return the entire list of brands for the filter
   */
  public getBrandsAsFilteredList(): string[] {
    return this.brands.map((brand: AutoCompleteMapping) => {
      return brand.key;
    }) as string[];
  }

  /**
   * Default callback on focus
   */
  public onFocus(): void {
    if (this.enableQuickDropDownMode) {
      this.filteredList = this.getBrandsAsFilteredList();
      this.autoComplete.value = this.innerValue;
      this.autoComplete.highlightOptionChanged = true;
      this.autoComplete.show();
    }
  }
}
